import { useEffect, useState, lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { safeLog } from '@/utils/safeLogger';
import { Button } from '@/components/ui/button';
import { AccessDenied } from './AccessDenied';
import { QRCodeLogin } from './QRCodeLogin';
import { ThemeProvider } from "next-themes";

// Debug Info Component
const DebugInfo = ({ user, session, loading, isOfficeReady }: any) => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-muted">
    <div className="text-center flex flex-col gap-3">
      <h2 className="text-xl font-semibold text-destructive">DEBUG INFO</h2>
      <div className="text-xs text-left bg-card p-4 rounded-lg border border-border font-mono">
        <p className="mb-2">User: {user?.email || 'none'}</p>
        <p className="mb-2">Session: {session ? 'YES' : 'NO'}</p>
        <p className="mb-2">Loading: {loading ? 'YES' : 'NO'}</p>
        <p>Office Ready: {isOfficeReady ? 'YES' : 'NO'}</p>
      </div>
    </div>
  </div>
);

// Lazy load AddInTaskPane for performance
const LazyAddInTaskPane = lazy(() => 
  import('./AddInTaskPane').then(m => ({ default: m.AddInTaskPane }))
);

const OfficeInitLoading = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-4">
    <Loader2 className="w-8 h-8 text-primary animate-spin" />
    <p className="text-sm text-muted-foreground">Initializing Office Add-In...</p>
  </div>
);

const TaskPaneLoading = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-4">
    <Loader2 className="w-6 h-6 text-primary animate-spin" />
    <p className="text-sm text-muted-foreground">Loading add-in...</p>
  </div>
);

const ErrorFallback = ({ error, resetErrorBoundary }: any) => {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-destructive mb-2">Something went wrong</h2>
        <p className="text-sm text-muted-foreground mb-4">{error?.message || 'An unexpected error occurred'}</p>
        {user && <p className="text-xs text-muted-foreground">Logged in as: {user.email}</p>}
      </div>
      <Button onClick={resetErrorBoundary}>
        Try Again
      </Button>
    </div>
  );
};

const LoginPrompt = ({ onLoginClick }: { onLoginClick: () => void }) => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
    <div className="text-center flex flex-col gap-3">
      <h2 className="text-xl font-semibold text-foreground">Welcome to Contact Extractor</h2>
      <p className="text-sm text-muted-foreground">
        Please log in to extract and save contact information from emails.
      </p>
    </div>
    <Button 
      onClick={onLoginClick} 
      size="lg" 
      className="w-full max-w-xs"
    >
      Login
    </Button>
  </div>
);

export const AddInWrapper = () => {
  const [isOfficeReady, setIsOfficeReady] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogInstance, setDialogInstance] = useState<Office.Dialog | null>(null);
  const { user, session, loading } = useAuth();
  const [showDebug, setShowDebug] = useState(false);

  // Log auth state for debugging (no PII)
  useEffect(() => {
    safeLog.dev('🔐 AddInWrapper Auth State:', {
      hasUser: !!user,
      hasSession: !!session,
      hasAccessToken: !!session?.access_token,
      loading
    });
  }, [user, session, loading]);


  useEffect(() => {
    safeLog.info('=== ADD-IN WRAPPER MOUNTED ===');
    
    if ((window as any).__OFFICE_READY__) {
      safeLog.info('✅ Office already ready (from index.html)');
      
      const Office = (window as any).Office;
      if (!Office?.context?.mailbox) {
        safeLog.error('❌ NOT IN OUTLOOK CONTEXT - Access Denied');
        (window as any).__ACCESS_DENIED__ = true;
        setIsOfficeReady(false);
        return;
      }
      
      setIsOfficeReady(true);
      return;
    }
    
    let timeoutId: NodeJS.Timeout;
    
    if (typeof (window as any).Office !== 'undefined') {
      safeLog.info('🔄 Calling Office.onReady() from AddInWrapper...');
      (window as any).Office.onReady((info: any) => {
        safeLog.info(`✅ Office.js ready (from AddInWrapper): ${info?.host}`);
        
        const Office = (window as any).Office;
        if (!Office?.context?.mailbox) {
          safeLog.error('❌ NOT IN OUTLOOK CONTEXT - Access Denied');
          (window as any).__ACCESS_DENIED__ = true;
          setIsOfficeReady(false);
          return;
        }
        
        setIsOfficeReady(true);
      });

      timeoutId = setTimeout(() => {
        safeLog.warn('⚠️ Office.onReady timeout - forcing ready state');
        setIsOfficeReady(true);
      }, 3000);
    } else {
      safeLog.error('❌ Office.js not available');
      setIsOfficeReady(true);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const openLoginDialog = () => {
    const Office = (window as any).Office;
    
    if (!Office?.context?.mailbox) {
      safeLog.error('❌ Not in Outlook context - cannot open auth dialog');
      return;
    }
    
    if (!Office?.context?.ui) {
      safeLog.warn('❌ Office.context.ui not available - cannot open dialog');
      return;
    }

    const loginUrl = `${window.location.origin}/auth?mode=dialog&redirect=/addin`;
    safeLog.info('🔓 Opening login dialog:', { loginUrl });
    
    Office.context.ui.displayDialogAsync(
      loginUrl,
      { height: 85, width: 65, promptBeforeOpen: true },
      (asyncResult: Office.UIResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          const dialog = asyncResult.value;
          setDialogInstance(dialog);
          setIsDialogOpen(true);
          safeLog.info('✅ Login dialog opened');
          
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            async (arg: any) => {
              try {
                const message = JSON.parse(arg.message);
                if (message.status === 'success') {
                  safeLog.info('✅ Login successful, received session from dialog');
                  
                  if (message.session?.access_token && message.session?.refresh_token) {
                    safeLog.info('💾 Setting session in add-in from dialog...');
                    
                    try {
                      await supabase.auth.setSession({
                        access_token: message.session.access_token,
                        refresh_token: message.session.refresh_token
                      });
                      
                      safeLog.info('✅ Session set successfully!');
                      
                      message.session.access_token = '';
                      message.session.refresh_token = '';
                      message.session = null;
                    } catch (error) {
                      safeLog.error('❌ Failed to set session', error as Error);
                    }
                  } else {
                    safeLog.warn('⚠️ No session data in message from dialog');
                  }
                  
                  dialog.close();
                  setIsDialogOpen(false);
                  setDialogInstance(null);
                  
                  safeLog.info('🔄 Reloading to apply session...');
                  window.location.reload();
                }
              } catch (error) {
                safeLog.error('❌ Error parsing dialog message', error as Error);
              }
            }
          );

          dialog.addEventHandler(
            Office.EventType.DialogEventReceived,
            (arg: any) => {
              safeLog.info('Dialog event:', arg);
              setIsDialogOpen(false);
              setDialogInstance(null);
            }
          );
        } else {
          safeLog.error('❌ Dialog open failed:', asyncResult.error as any);
          setIsDialogOpen(false);
        }
      }
    );
  };


  if ((window as any).__ACCESS_DENIED__) {
    return <AccessDenied />;
  }

  if (!isOfficeReady) {
    return <OfficeInitLoading />;
  }

  if (loading) {
    return <TaskPaneLoading />;
  }

  const hasValidSession = user && 
                          session?.access_token && 
                          session?.expires_at && 
                          session.expires_at > Math.floor(Date.now() / 1000);

  if (!hasValidSession) {
    safeLog.info('🔒 AddInWrapper: No valid session - showing QR login');
    safeLog.dev('🔒 Validation details:', {
      hasUser: !!user,
      hasAccessToken: !!session?.access_token,
      expiresAt: session?.expires_at ? new Date(session.expires_at * 1000) : null,
      isExpired: session?.expires_at ? session.expires_at <= Math.floor(Date.now() / 1000) : 'no timestamp'
    });
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <QRCodeLogin onAuthSuccess={() => window.location.reload()} />
      </div>
    );
  }

  safeLog.dev('✅ AddInWrapper: User authenticated');

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error) => safeLog.error('[AddIn Error]', error)}
        onReset={() => window.location.reload()}
      >
        <Suspense fallback={<TaskPaneLoading />}>
          <LazyAddInTaskPane />
        </Suspense>
      </ErrorBoundary>
    </ThemeProvider>
  );
};
